/**
 * English strings for the Personal Settings (Account) page.
 * Kept out of the main translation file to stay within the size gate.
 */
import { enConnectedAccounts, enConnectedApps } from "./enMcpConsent";

export const enAccount = {
	search: "Search...",
	name: "Name",
	companyName: "Company Name",
	jobTitle: "Job Title",
	connectedOn: "Connected on",
	connectionScraping: "LinkedIn-Connections",
	enableConnectionScraping: "active the scraping of your linkedin connections",
	enableConnectionScrapingDescription:
		"This allows you to identify which prospects you or your colleagues are already connected to on Linkedin.",
	syncInterval: "Sync Interval",
	syncIntervalDaily: "Daily",
	syncIntervalWeekly: "Weekly",
	syncIntervalMonthly: "Monthly",
	lastSynced: "Last synced",
	neverSynced: "Never synced",
	syncFromLinkedin: "Sync from LinkedIn",
	connectionSyncStarted: "Connection sync started. You'll be notified when it's complete.",
	connectionSyncError: "Failed to sync connections",
	autoWithdrawInvites: "Auto-Withdraw Connection Requests",
	enableAutoWithdrawInvites: "Automatically withdraw stale connection requests",
	enableAutoWithdrawInvitesDescription:
		"Connection requests sent through Signalist that are still pending after the selected number of days are automatically withdrawn.",
	autoWithdrawInvitesWindow: "Withdraw after",
	autoWithdrawInvitesDaysOption: "{{days}} days",
	linkedinAutomation: "LinkedIn Automation",
	enableAutoAccept: "Enable Auto Accept",
	enableAutoAcceptDescription: "Automatically accept connection requests",
	profileVisits: "Profile Visits",
	warningPremiumAccountRequired: "This feature is only available for premium accounts",
	warningSalesnavigatorRequired: "This feature requires LinkedIn Sales Navigator",
	enableProfileVisitsScraping: "Allow Profile Visits Scraping",
	hasLiSn: "LinkedIn Sales Navigator",
	uploadConnections: "Upload Connections",
	changePassword: "Change password",
	currentPassword: "Current password",
	disableTwoFactorAuth: "Disable 2FA",
	enableTwoFactorAuth: "Enable 2FA",
	enableTwoFactorAuthDescription: "Scan the QR code with your authenticator app",
	cannotScanQrCode: "Can't scan the QR code?",
	manualSetupHint:
		"Enter this key manually in your authenticator app. If the app asks for a type, choose time-based.",
	manualSetupKey: "Setup key",
	copyKey: "Copy key",
	keyCopied: "Copied",
	newPassword: "New Password",
	profileInformation: "Profile information",
	profileInformationSubtitle: "Your personal details and how you sign in.",
	profileSecurity: "Profile security",
	changeEmail: "Change email",
	saved: "Saved",
	twoFactorAuth: "Two-factor authentication",
	enableTwoFactorAuthHint: "Add an extra layer of security with an authenticator app.",
	repeatPassword: "Repeat new password",
	copywriting: {
		title: "Copywriting settings",
		subtitle: "Choose the languages the AI copywriting agent is allowed to write in.",
		resolutionHint:
			"If a lead speaks your primary language — stated on their LinkedIn profile, or likely " +
			"from the country they live in — the agent writes in that language. If not, but they " +
			"speak one of your secondary languages, the agent uses that one. Only when neither " +
			"matches does it fall back to your fallback language.",
	},
	tabs: {
		general: "General",
		connectedAccounts: "Connected Accounts",
		notifications: "Notifications",
		linkedinConnections: "My LinkedIn Connections",
	},
	accountStatus: {
		title: "Account status",
		subtitle: "Your role determines what you can do from your connected accounts.",
		sendingStatus: "Sending status",
		sendingDescription:
			"As a sending user you can run campaigns and outreach from your connected accounts.",
		nonSendingDescription:
			"Campaign sending is managed by your admin. You can still upload connections and enrich contacts.",
		sendingUser: "Sending user",
		nonSendingUser: "Non-sending user",
		manageInCompanySettings: "Manage in Company Settings",
	},
	connectionEnrichment: {
		title: "Connection enrichment",
		subtitle: "Turn your LinkedIn network into enriched, ready-to-use contacts.",
		enrichToggle: "Enrich my LinkedIn connections",
		enrichDescription:
			"We enrich your connections into a private list — adding company, role, country and a lead score — and keep it up to date.",
		noLinkedinAccount: "Connect a LinkedIn account first to enrich your connections.",
		pausedListNote: "Paused: your connection list is paused, so new connections are not enriched.",
		pausedListResume: "Resume the list to keep it up to date.",
		howManySubhead: "How many should we enrich?",
		howManyAll: "All connections",
		howManyRecent1000: "Most recent 1,000",
		howManyRecent2000: "Most recent 2,000",
		howManyMonthly: "Set a monthly limit",
		howManyNewOnly: "New connections only",
		monthlyLimitLabel: "Per month",
		estimating: "Estimating credit cost…",
		costPreview:
			"Enriching {{count}} connections costs about {{credits}} credits ({{available}} available).",
		teamVisibleToggle: "Make enriched connections visible to my team",
		teamVisibleDescription:
			"When on, your enriched connections are shared with your team. When off, they stay private to you until they're also used on another list.",
	},
	connectedApps: enConnectedApps,
	connectedAccounts: enConnectedAccounts,
	myConnections: {
		filterAll: "All",
		filterQualified: "Qualified (P1–P3)",
		filterDisqualified: "Disqualified (DQ)",
		showAll: "All connections",
		showEnriched: "Only enriched",
		showNonEnriched: "Only non-enriched",
		showDropdownLabel: "Show",
		qualifiedDropdownLabel: "Qualified",
		filterAny: "Any",
		filterScore: "Score",
		filterEnrichment: "Enrichment",
		filterCompanyName: "Company name",
		filterCompanyNamePlaceholder: "Contains…",
		filterJobTitle: "Job title",
		filterJobTitlePlaceholder: "Contains…",
		filterCountry: "Country",
		filterSeniority: "Seniority",
		filterLanguage: "Language",
		filterCompanyIndustry: "Company industry",
		filterCompanyCountry: "Company country",
		filterCompanySize: "Company size",
		filterConnectedOn: "Connected on",
		filterConnectedOnPlaceholder: "Any date",
		filterReset: "Reset filters",
		columns: "Columns",
		filter: "Filter",
		personalCountry: "Personal Country",
		language: "Language",
		enrich: "Enrich",
		enrichDisabledTooltip: 'Enable "Enrich my LinkedIn connections" to use this feature',
		blacklist: "Blacklist",
		export: "Export",
		addTo: "Add to",
		rowsOf: "Rows: {{shown}} / {{total}}",
		enrichSuccess: "Enrichment started for {{count}} connection(s)",
		enrichError: "Failed to start enrichment",
		blacklistSuccess_one: "{{count}} connection added to blacklist",
		blacklistSuccess_other: "{{count}} connections added to blacklist",
		blacklistError: "Failed to add connections to blacklist",
		addToListSuccess: "Connection(s) added to list",
		addToListError: "Failed to add connections to list",
		inlineEnrich: "Enrich",
		seniority: "Seniority",
		gender: "Gender",
		salutationDe: "Salutation (DE)",
		salutationEn: "Salutation (EN)",
		location: "Location",
		companyCountry: "Company Country",
		companyIndustry: "Company Industry",
		companySize: "Company Size",
		companyFoundedYear: "Founded Year",
		personaCategory: "Persona Category",
		companyCategory: "Company Category",
	},
	title: "Personal Settings",
	linkedinConnections: "LinkedIn Connections",
	linkedinCapabilities: {
		title: "LinkedIn account status",
		description:
			"These values are detected automatically from your connected LinkedIn account and cannot be changed manually.",
		premium: "LinkedIn Premium",
		salesNavigator: "LinkedIn Sales Navigator",
		companyPageAdmin: "Company page admin",
		detected: "We detected the following for your LinkedIn account:",
		recheck: "Recheck",
		notConnected: "No LinkedIn account connected",
		recheckFailed: "Could not recheck the LinkedIn account. Please try again later.",
	},
};
